import {
  ArrowPathIcon,
  BuildingOffice2Icon,
  ChevronLeftIcon,
  ChevronRightIcon,
  HomeIcon,
  MagnifyingGlassIcon,
  PhotoIcon,
} from '@heroicons/react/24/outline';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { useSelector } from 'react-redux';

import type { ValuationHistoryItem, ValuationHistoryResponse } from '../../../@types/Valuation';
import type { RootState } from '../../../store/store';
import { JWTHeader } from '../../../utils/JWTHeader';
import Spinner from '../../common/Spinner';

export interface ValuationHistoryHandle {
  refresh: () => void;
}

interface Props {
  companyId: number;
  coproId: number;
  onSelect: (item: ValuationHistoryItem) => void;
  accentColor?: string;
}

const ITEMS_PER_PAGE = 10;

const fmtCurrency = (n: number) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);

const fmtDate = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const ValuationHistory = forwardRef<ValuationHistoryHandle, Props>(({ companyId, coproId, onSelect, accentColor = '#134094' }, ref) => {
  const jwt = useSelector((state: RootState) => state.auth.token);

  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [data, setData] = useState<ValuationHistoryResponse | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
      setCurrentPage(1);
    }, 300);

    return () => window.clearTimeout(timeout);
  }, [searchInput]);

  const fetchHistory = useCallback(async() => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        step: String(currentPage),
        limit: String(ITEMS_PER_PAGE),
        sortField: 'createdAt',
        sortDirection: 'desc',
      });
      if (debouncedSearch) {
        params.set('filterSearch', debouncedSearch);
      }

      const url = `/api/companies/${companyId}/copros/${coproId}/valuation/history?${params.toString()}`;
      const resp = await fetch(url, JWTHeader(jwt));
      if (resp.ok) {
        const json: ValuationHistoryResponse = await resp.json();
        setData(json);
      }
    } catch {
      // silently ignore
    } finally {
      setLoading(false);
    }
  }, [companyId, coproId, currentPage, debouncedSearch, jwt]);

  useImperativeHandle(ref, () => ({
    refresh: () => { void fetchHistory(); },
  }), [fetchHistory]);

  useEffect(() => {
    void fetchHistory();
  }, [fetchHistory]);

  const results = data?.results ?? [];
  const pagination = data?.pagination;

  return (
    <div>
      {/* Search input */}
      <div className="relative mb-3">
        <MagnifyingGlassIcon className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Rechercher par vendeur, type..."
          className="w-full rounded-lg border border-slate-300 py-2 pr-3 pl-9 text-sm focus:border-transparent focus:ring-2"
          style={{ ['--tw-ring-color' as string]: accentColor }}
        />
      </div>

      {/* Content */}
      <div className="max-h-[40vh] overflow-y-auto">
        {loading && results.length === 0 && (
          <div className="flex items-center justify-center py-8">
            <Spinner />
          </div>
        )}

        {!loading && results.length === 0 && (
          <p className="py-6 text-center text-sm text-slate-400">Aucun avis de valeur généré pour cette copropriété.</p>
        )}

        {results.length > 0 && (
          <div className="space-y-2">
            {results.map((item) => {
              const pd = item.propertyData;
              return (
                <div
                  key={item.id}
                  className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-3 transition hover:shadow-sm"
                >
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-slate-100">
                    {pd.type === 'house'
                      ? <HomeIcon className="h-5 w-5 text-slate-500" />
                      : <BuildingOffice2Icon className="h-5 w-5 text-slate-500" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-800">
                        {pd.type === 'apartment' ? 'Appartement' : 'Maison'} — {pd.rooms} p.
                      </span>
                      {pd.area && <span className="text-xs text-slate-400">{pd.area} m²</span>}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-slate-500">
                      <span>{fmtCurrency(pd.price)} – {fmtCurrency(pd.priceMax)}</span>
                      {pd.seller && <span>· {pd.seller}</span>}
                      {item.photoUrls.length > 0 && (
                        <span className="inline-flex items-center gap-0.5">
                          <PhotoIcon className="h-3 w-3" /> {item.photoUrls.length}
                        </span>
                      )}
                      <span className="text-slate-400">{fmtDate(item.createdAt)}</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="flex-shrink-0 rounded-lg px-3 py-1.5 text-xs font-medium text-white transition hover:brightness-110"
                    style={{ backgroundColor: accentColor }}
                    onClick={() => onSelect(item)}
                  >
                    <ArrowPathIcon className="mr-1 inline h-3.5 w-3.5" />
                    Reprendre
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-3">
          <span className="text-xs text-slate-400">
            {pagination.totalResults} résultat{pagination.totalResults > 1 ? 's' : ''}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={!pagination.previousPage}
              className="rounded p-1 text-slate-500 hover:bg-slate-100 disabled:opacity-30"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </button>
            <span className="px-2 text-xs text-slate-600">
              {pagination.currentPage} / {pagination.totalPages}
            </span>
            <button
              type="button"
              disabled={!pagination.nextPage}
              className="rounded p-1 text-slate-500 hover:bg-slate-100 disabled:opacity-30"
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
});

ValuationHistory.displayName = 'ValuationHistory';

export default ValuationHistory;
